import { useEffect, useRef } from 'react';

const animationStyles = {
  'fade-up': {
    start: { opacity: '0', transform: 'translateY(40px)' },
    end: { opacity: '1', transform: 'translateY(0)' },
  },
  'fade-down': {
    start: { opacity: '0', transform: 'translateY(-40px)' },
    end: { opacity: '1', transform: 'translateY(0)' },
  },
  'fade-left': {
    start: { opacity: '0', transform: 'translateX(-40px)' },
    end: { opacity: '1', transform: 'translateX(0)' },
  },
  'fade-right': {
    start: { opacity: '0', transform: 'translateX(40px)' },
    end: { opacity: '1', transform: 'translateX(0)' },
  },
  'scale': {
    start: { opacity: '0', transform: 'scale(0.8)' },
    end: { opacity: '1', transform: 'scale(1)' },
  },
  'zoom': {
    start: { opacity: '0', transform: 'scale(0.5) rotate(-5deg)' },
    end: { opacity: '1', transform: 'scale(1) rotate(0)' },
  },
  'none': {
    start: {},
    end: {},
  },
};

let globalObserver = null;
const elementsMap = new Map();

const handleIntersection = (entries) => {
  entries.forEach((entry) => {
    const data = elementsMap.get(entry.target);
    if (!data) return;

    const { endStyles, startStyles, once, animated } = data;

    if (entry.isIntersecting && !animated) {
      data.animated = true;
      Object.assign(entry.target.style, endStyles);

      if (once && globalObserver) {
        globalObserver.unobserve(entry.target);
        elementsMap.delete(entry.target);
      }
    }

    if (!once && !entry.isIntersecting) {
      data.animated = false;
      Object.assign(entry.target.style, startStyles);
    }
  });
};

const getObserver = () => {
  if (!globalObserver) {
    globalObserver = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      rootMargin: '0px',
    });
  }
  return globalObserver;
};

export const useScrollAnimation = (options = {}) => {
  const {
    animation = 'fade-up',
    duration = 600,
    delay = 0,
    once = true,
    customStartStyles = {},
    customEndStyles = {},
  } = options;

  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const anim = animationStyles[animation] || animationStyles['fade-up'];

    const startStyles = {
      ...anim.start,
      ...customStartStyles,
    };

    const endStyles = {
      ...anim.end,
      ...customEndStyles,
    };

    Object.assign(el.style, {
      ...startStyles,
      transition: `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
      transitionDelay: `${delay}ms`,
      willChange: 'transform, opacity',
    });

    const observer = getObserver();

    elementsMap.set(el, {
      startStyles,
      endStyles,
      once,
      animated: false,
    });

    observer.observe(el);

    return () => {
      observer.unobserve(el);
      elementsMap.delete(el);
    };
  }, [animation, duration, delay, once, customStartStyles, customEndStyles]);

  return ref;
};

export const cleanupScrollAnimation = () => {
  if (globalObserver) {
    globalObserver.disconnect();
    globalObserver = null;
    elementsMap.clear();
  }
};